export const publicNav = [
  { label: "Weather", href: "/en/weather" },
  { label: "Marinas", href: "/en/marinas" },
  { label: "Tools", href: "/en/sailing-calculators" },
  { label: "Learn", href: "/en/learning-center" },
  { label: "Destinations", href: "/en/destinations" },
  { label: "Wire", href: "/en/wire" },
];

export const quickAccess = [
  { title: "Marine Weather", href: "/en/weather", description: "Check sailor-friendly marine forecasts before you go." },
  { title: "Marinas", href: "/en/marinas", description: "Discover marinas, services, destinations, and sailing hotspots." },
  { title: "Route Planning", href: "/en/sailing-calculators/eta-calculator", description: "Plan smarter passages with simple nautical tools." },
  { title: "Sailing Guides", href: "/en/learning-center", description: "Learn navigation, seamanship, anchoring, and marine weather." },
  { title: "Nautical Tools", href: "/en/sailing-calculators", description: "Use practical calculators for distance, fuel, ETA, and anchoring." },
  { title: "Anchorages", href: "/en/anchorages-near-me", description: "Explore safe places to stop, rest, and enjoy the coast." },
];

export const featuredGuides = [
  "How to Read a Marine Weather Forecast",
  "Beginner’s Guide to Sailing Navigation",
  "How Much Anchor Chain Do You Need?",
  "What Is Hull Speed?",
  "How to Plan a Short Coastal Passage",
];

export const learningTopics = [
  { slug: "navigation-positioning", title: "Navigation & Positioning", description: "Charts, bearings, GPS, coordinates, passage awareness, and position checks." },
  { slug: "safety-emergency", title: "Safety & Emergency", description: "Mayday basics, onboard readiness, emergency decisions, and crew safety." },
  { slug: "seamanship", title: "Seamanship", description: "Boat handling, docking, anchoring, line work, and practical skipper habits." },
  { slug: "weather-meteorology", title: "Weather & Meteorology", description: "Wind, gusts, waves, pressure, forecasts, and go/no-go planning." },
  { slug: "communication", title: "Communication", description: "VHF procedure, marina calls, radio etiquette, and clear onboard coordination." },
  { slug: "regulations-legal", title: "Regulations & Legal", description: "Local rules, documents, port entry, safety requirements, and compliance basics." },
  { slug: "maintenance-equipment", title: "Maintenance & Equipment", description: "Routine checks, gear care, essential equipment, and fault prevention." },
  { slug: "marine-systems", title: "Marine Systems", description: "Engines, batteries, plumbing, electronics, and onboard system fundamentals." },
  { slug: "practical-skills", title: "Practical Skills", description: "Everyday tasks, planning habits, knots, checks, and confidence-building skills." },
  { slug: "advanced-topics", title: "Advanced Topics", description: "Offshore decisions, complex systems, advanced navigation, and high-confidence skipper skills." },
];

export const learningTopicDetails = {
  "navigation-positioning": {
    h1: "Navigation & Positioning",
    intro: "Learn how sailors understand position, direction, charts, bearings, GPS, and safe route awareness before and during a passage.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/eb3e66e59_Sextant.JPG",
    bullets: ["Chart reading basics", "GPS and coordinates", "Bearings and fixes", "Passage awareness"],
    sections: [
      { title: "Know where you are", description: "Use latitude, longitude, chart symbols, depth references, and visual landmarks to maintain reliable position awareness." },
      { title: "Use bearings and routes", description: "Understand headings, bearings, course over ground, waypoints, and how to compare planned routes with real movement." },
      { title: "Build safe navigation habits", description: "Cross-check electronic tools with visual observations, weather, depth, and local hazards before committing to a route." },
    ],
  },
  "safety-emergency": {
    h1: "Safety & Emergency",
    intro: "Build confidence with practical safety preparation, crew briefing habits, distress awareness, and emergency decision-making.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/3788dd3ec_Safety.JPG",
    bullets: ["Crew briefing", "Mayday awareness", "Emergency equipment", "Calm decision-making"],
    sections: [
      { title: "Prepare before departure", description: "Check lifejackets, communication devices, first-aid gear, bilge systems, emergency contacts, and crew responsibilities." },
      { title: "Understand distress signals", description: "Learn when to use Mayday, Pan-Pan, VHF channels, flares, EPIRBs, and other urgent communication tools." },
      { title: "Respond under pressure", description: "Keep actions simple: stabilize the boat, protect people, communicate clearly, and choose the safest available option." },
    ],
  },
  seamanship: {
    h1: "Seamanship",
    intro: "Improve everyday boat handling, docking, anchoring, line work, watchkeeping, and practical skipper judgement.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/cd694430a_seamenship.JPG",
    bullets: ["Boat handling", "Docking basics", "Anchoring skills", "Skipper habits"],
    sections: [
      { title: "Handle the boat smoothly", description: "Practice speed control, turning space, wind influence, prop walk, and calm movements around docks and anchorages." },
      { title: "Use lines and anchors well", description: "Learn line handling, fenders, cleats, knots, anchoring scope, holding checks, and safe deck communication." },
      { title: "Think like a skipper", description: "Anticipate conditions, brief the crew, reduce surprises, and make conservative choices early." },
    ],
  },
  "weather-meteorology": {
    h1: "Weather & Meteorology",
    intro: "Understand wind, gusts, waves, pressure systems, forecasts, and the practical weather signals that matter at sea.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/40160f5da_weather.JPG",
    bullets: ["Wind and gusts", "Waves and swell", "Forecast reading", "Go/no-go planning"],
    sections: [
      { title: "Read the marine forecast", description: "Focus on wind direction, gust spread, wave height, swell period, visibility, pressure trends, and warning language." },
      { title: "Connect weather to boat handling", description: "Learn how wind shifts, gusts, sea state, and current affect comfort, speed, anchoring, docking, and passage timing." },
      { title: "Choose safer windows", description: "Compare forecasts, watch trends, plan alternatives, and delay departures when conditions are uncertain or worsening." },
    ],
  },
  communication: {
    h1: "Communication",
    intro: "Learn clear marine communication for VHF calls, marina arrivals, onboard coordination, and emergency situations.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/9ee193502_Communication.JPG",
    bullets: ["VHF basics", "Marina calls", "Crew coordination", "Radio etiquette"],
    sections: [
      { title: "Use VHF clearly", description: "Practice concise call structure, channel awareness, vessel identification, position reporting, and confirmation language." },
      { title: "Communicate with marinas", description: "Prepare arrival details, berth requests, boat length, draft, ETA, and instructions before entering busy harbor areas." },
      { title: "Keep the crew aligned", description: "Use simple commands, repeat-back confirmation, clear roles, and calm communication during docking or maneuvering." },
    ],
  },
  "regulations-legal": {
    h1: "Regulations & Legal",
    intro: "Understand the documents, safety rules, port procedures, and local requirements sailors should check before cruising.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/a444d6a44_legal.JPG",
    bullets: ["Boat documents", "Port entry", "Local rules", "Safety compliance"],
    sections: [
      { title: "Know required documents", description: "Review registration, insurance, skipper licenses, crew lists, passports, cruising permits, and marina paperwork." },
      { title: "Respect local procedures", description: "Check customs, port authority requirements, anchoring restrictions, marine protected areas, and navigation rules." },
      { title: "Plan compliance early", description: "Keep documents accessible, verify current rules before arrival, and avoid assumptions between countries or regions." },
    ],
  },
  "maintenance-equipment": {
    h1: "Maintenance & Equipment",
    intro: "Learn the checks, tools, spare parts, and equipment habits that reduce breakdowns and keep the boat ready.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/58cc40603_Equipment.JPG",
    bullets: ["Pre-departure checks", "Essential spares", "Gear care", "Fault prevention"],
    sections: [
      { title: "Check before you sail", description: "Inspect engine fluids, belts, batteries, bilges, seacocks, rigging, safety gear, lights, and navigation equipment." },
      { title: "Carry practical spares", description: "Think through filters, belts, fuses, bulbs, impellers, tools, tape, line, shackles, and repair basics for your boat." },
      { title: "Prevent small issues growing", description: "Log problems early, fix leaks and corrosion quickly, and make routine checks part of every passage habit." },
    ],
  },
  "marine-systems": {
    h1: "Marine Systems",
    intro: "Understand the onboard systems that support safe cruising, including engines, batteries, plumbing, electronics, and pumps.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/619f253ab_Engine.JPG",
    bullets: ["Engine basics", "Electrical systems", "Water and pumps", "Navigation electronics"],
    sections: [
      { title: "Understand propulsion", description: "Learn basic engine checks, cooling, fuel flow, warning signs, and what to monitor while underway." },
      { title: "Manage onboard power", description: "Track battery state, charging sources, high-draw devices, shore power habits, and simple electrical troubleshooting." },
      { title: "Know critical systems", description: "Identify bilge pumps, seacocks, freshwater systems, navigation instruments, and the controls you may need in a hurry." },
    ],
  },
  "practical-skills": {
    h1: "Practical Skills",
    intro: "Build everyday confidence with knots, checks, planning routines, watch habits, and small tasks that make sailing safer.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/4d838aaf4_skills.JPG",
    bullets: ["Knots and lines", "Daily checks", "Passage routines", "Confidence building"],
    sections: [
      { title: "Master useful basics", description: "Practice knots, fender placement, line handling, stowing gear, reading instruments, and preparing the cockpit." },
      { title: "Create simple routines", description: "Use repeatable checklists for departure, anchoring, docking, weather review, and end-of-day boat security." },
      { title: "Grow through repetition", description: "Improve by practicing in calm conditions, reviewing mistakes, and adding skills gradually instead of rushing complexity." },
    ],
  },
  "advanced-topics": {
    h1: "Advanced Topics",
    intro: "Develop higher-level seamanship with offshore planning, complex systems, advanced navigation, and confident skipper decision-making.",
    image: "https://media.base44.com/images/public/69e8e257032c3bb17c39059f/cec1b08c9_advanced.JPG",
    bullets: ["Advanced navigation", "Offshore planning", "Complex systems", "Skipper judgement"],
    sections: [
      { title: "Plan beyond the basics", description: "Connect weather windows, route options, crew endurance, systems readiness, and emergency alternatives before committing offshore." },
      { title: "Manage complex situations", description: "Use structured decisions when equipment, weather, fatigue, or traffic create overlapping risks." },
      { title: "Build professional habits", description: "Debrief passages, document lessons, refine checklists, and keep improving judgement through deliberate practice." },
    ],
  },
};

export const learningArticles = Object.fromEntries(
  learningTopics.map((topic) => [
    topic.slug,
    [
      {
        slug: `${topic.slug}-basics`,
        title: `${topic.title}: Essential Basics`,
        excerpt: `A clear introduction to the most important ${topic.title.toLowerCase()} concepts every sailor should understand.`,
        keyPoints: ["Start with fundamentals", "Use simple checks", "Practice before pressure", "Review often"],
        sections: [
          { heading: "Why it matters", body: `${topic.title} affects real decisions on board. Learning the basics helps sailors reduce confusion, communicate better, and prepare before conditions become demanding.` },
          { heading: "What to focus on first", body: `Begin with the simple tasks you can repeat every trip: observation, preparation, clear communication, and checking information before acting.` },
          { heading: "How to practice", body: `Use calm days to rehearse the skill, ask what could go wrong, and build a short routine you can repeat before every departure.` },
        ],
        faq: [
          { q: "Is this suitable for beginners?", a: "Yes. The lesson is written for new sailors and practical skippers." },
          { q: "Can I use this as a checklist?", a: "Yes. Treat the key points as a starting checklist and adapt it to your boat." },
        ],
      },
      {
        slug: `${topic.slug}-practical-guide`,
        title: `${topic.title}: Practical Guide`,
        excerpt: `A practical onboard guide for applying ${topic.title.toLowerCase()} during preparation, passage planning, and daily sailing.`,
        keyPoints: ["Prepare early", "Keep tasks visible", "Confirm with crew", "Choose conservative actions"],
        sections: [
          { heading: "Before departure", body: `Review the situation, check the relevant tools and documents, brief the crew, and decide what would make you delay or change the plan.` },
          { heading: "During the trip", body: `Stay aware of changes, keep communication short and clear, and compare what you expected with what is actually happening around the boat.` },
          { heading: "After arrival", body: `Review what worked, write down issues, update your routine, and prepare improvements for the next trip.` },
        ],
        faq: [
          { q: "How often should I review this?", a: "Review it before each relevant passage or training session." },
          { q: "Does this replace official rules?", a: "No. Always verify with official guidance, charts, forecasts, and local regulations." },
        ],
      },
      {
        slug: `${topic.slug}-mistakes`,
        title: `${topic.title}: Common Mistakes`,
        excerpt: `Common mistakes sailors make with ${topic.title.toLowerCase()} and how to avoid them with simple routines.`,
        keyPoints: ["Avoid rushing", "Cross-check information", "Brief the crew", "Leave safety margin"],
        sections: [
          { heading: "Rushing decisions", body: `Many problems start when decisions are made late or under pressure. Slow down early, simplify the plan, and choose the safer option.` },
          { heading: "Relying on one source", body: `Avoid depending on one instrument, one forecast, or one assumption. Cross-check with another source whenever possible.` },
          { heading: "Skipping the debrief", body: `A short review after the trip turns mistakes into useful learning and helps the next passage feel calmer.` },
        ],
        faq: [
          { q: "What is the safest habit?", a: "Prepare early, communicate clearly, and leave margin for changing conditions." },
          { q: "Should beginners memorize everything?", a: "No. Start with simple repeatable routines and build knowledge gradually." },
        ],
      },
    ],
  ])
);

export const tools = [
  { slug: "fuel-calculator", title: "Boat Fuel Calculator", description: "Estimate fuel needs before a coastal passage.", formula: "Fuel = distance ÷ speed × burn rate" },
  { slug: "eta-calculator", title: "Sailing ETA Calculator", description: "Estimate arrival time using distance and speed.", formula: "ETA = distance ÷ speed" },
  { slug: "distance-calculator", title: "Nautical Distance Calculator", description: "Convert passage distance into practical planning numbers.", formula: "1 nautical mile = 1.852 km" },
  { slug: "anchor-scope", title: "Anchor Scope Calculator", description: "Calculate safe anchor rode length for common conditions.", formula: "Scope = water depth × ratio" },
  { slug: "apparent-wind", title: "Apparent Wind Calculator", description: "Understand the wind felt onboard while moving.", formula: "Apparent wind combines true wind and boat speed" },
  { slug: "hull-speed", title: "Hull Speed Calculator", description: "Estimate displacement hull speed from waterline length.", formula: "Hull speed = 1.34 × √LWL" },
];

export const destinations = ["Greece", "Croatia", "Italy", "Turkey", "Mediterranean Hotspots"];

export const wireArticles = [
  { slug: "read-marine-weather-before-sailing", title: "How Sailors Should Read Marine Weather Before Departure", category: "Weather", readTime: "6 min", excerpt: "A practical guide to wind, gusts, waves, and safe go/no-go decisions." },
  { slug: "mediterranean-sailing-season-guide", title: "Mediterranean Sailing Season Guide", category: "Destinations", readTime: "5 min", excerpt: "When to sail Greece, Croatia, Italy, and Turkey with calmer conditions." },
  { slug: "marina-arrival-checklist", title: "Marina Arrival Checklist for Skippers", category: "Marinas", readTime: "4 min", excerpt: "Simple steps for safer arrivals, docking, VHF calls, and berth planning." },
];

export const pageContent = {
  weather: {
    h1: "Marine Weather Forecast for Sailors, Boaters & Coastal Trips",
    intro: "Check a sailor-friendly weather forecast with wind, gusts, waves, swell, visibility, tide context, marina departure notes and practical go/no-go safety guidance.",
    bullets: ["Marine weather forecast for sailing", "Wind and gust forecast for boating", "Wave, swell and visibility checks", "Departure safety notes near marinas"],
  },
  marinas: {
    h1: "Marinas in the Mediterranean — Services, Locations & Sailing Tips",
    intro: "Discover marina regions, arrival guidance, services, and practical planning tips for sailors.",
    bullets: ["Greece, Croatia, Italy, and Turkey", "Fuel, water, electricity, and visitor services", "Marina arrival checklist", "Nearby weather and sailing guides"],
  },
  tools: {
    h1: "Nautical Tools for Sailing, Fuel, ETA & Anchoring",
    intro: "Simple calculators designed for real onboard planning without clutter.",
    bullets: ["Fuel planning", "ETA and distance", "Anchor scope", "Hull speed and apparent wind"],
  },
  learn: {
    h1: "Sailing Guides for Beginners and Skippers",
    intro: "Learn navigation, seamanship, anchoring, marine weather, and voyage planning with clear practical guides.",
    bullets: ["Sailing basics", "Navigation", "Marine weather", "Anchoring and safety"],
  },
  destinations: {
    h1: "Sailing Destinations in Greece, Croatia, Italy & Turkey",
    intro: "Explore inspiring Mediterranean sailing regions with practical seasonal and marina planning advice.",
    bullets: ["Best seasons", "Suggested routes", "Beginner suitability", "Marinas and anchorages"],
  },
};